import { useState } from 'react';
import { ChevronDown, ChevronUp, MapPin, Calendar, ExternalLink } from 'lucide-react';
import { JobRole } from '../../types';
import KudosGrid from './KudosGrid';

interface ExperienceTimelineProps {
  experience: JobRole[];
}

export default function ExperienceTimeline({ experience }: ExperienceTimelineProps) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(
    new Set(experience.map(job => job.id))
  );

  const toggleExpanded = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  return (
    <section className="bg-gray-50 py-12 dark:bg-gray-900">
      <div className="container">
        <h2 className="mb-8 text-3xl font-bold text-gray-900 dark:text-gray-50">Experience</h2>

        <div className="relative space-y-8">
          {/* Timeline line */}
          <div className="absolute left-4 top-0 h-full w-0.5 bg-gray-300 md:left-8 dark:bg-gray-700" />

          {experience.map((job, _index) => {
            const isExpanded = expandedIds.has(job.id);

            return (
              <div key={job.id} className="relative">
                {/* Timeline dot */}
                <div className="absolute left-2 top-6 h-4 w-4 rounded-full border-4 border-white bg-primary-600 md:left-6 dark:border-gray-900 dark:bg-primary-400" />

                <div className="ml-12 md:ml-20">
                  <div className="card">
                    <div className="cursor-pointer" onClick={() => toggleExpanded(job.id)}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-gray-900">{job.title}</h3>
                          <p className="text-lg font-semibold text-primary-600">{job.company}</p>

                          <div className="mt-2 flex flex-wrap gap-4 text-sm text-gray-600">
                            <span className="flex items-center gap-1">
                              <Calendar size={16} />
                              {job.duration}
                            </span>
                            <span className="flex items-center gap-1">
                              <MapPin size={16} />
                              {job.location}
                            </span>
                          </div>
                        </div>

                        <button
                          className="ml-4 rounded-lg p-2 transition-colors hover:bg-gray-100 dark:hover:bg-gray-800"
                          aria-label={isExpanded ? 'Collapse' : 'Expand'}
                        >
                          {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                        </button>
                      </div>

                      <p className="mt-3 text-gray-700 dark:text-gray-300">{job.description}</p>
                    </div>

                    {isExpanded && (
                      <div className="mt-6 space-y-6 border-t border-gray-200 pt-6">
                        {/* Achievements */}
                        {job.achievements.length > 0 && (
                          <div>
                            <h4 className="mb-3 font-semibold text-gray-900">Key Achievements</h4>
                            <ul className="space-y-2">
                              {job.achievements.map((achievement, i) => (
                                <li key={i} className="flex items-start gap-2 text-gray-700 dark:text-gray-300">
                                  <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-primary-600 dark:bg-primary-400" />
                                  <span>{achievement}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Links */}
                        {job.links && job.links.length > 0 && (
                          <div>
                            <h4 className="mb-3 font-semibold text-gray-900 dark:text-gray-50">Links</h4>
                            <div className="flex flex-wrap gap-3">
                              {job.links.map((link, i) => (
                                <a
                                  key={i}
                                  href={link.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 hover:text-primary-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-primary-400"
                                >
                                  <ExternalLink size={16} />
                                  {link.label}
                                </a>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Kudos */}
                        {job.kudos.length > 0 && (
                          <div>
                            <h4 className="mb-3 font-semibold text-gray-900">
                              Recognition & Kudos ({job.kudos.length})
                            </h4>
                            <KudosGrid kudos={job.kudos} />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
